import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Print,
  CheckCircle,
  LocalShipping,
  EditOutlined,
  FileDownloadOutlined,
  TableChartOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { ShipmentStatus } from '../../types/api';
import { printShipment } from '../../utils/shipmentPrint';
import * as XLSX from 'xlsx';

interface ShipmentItem {
  id: number;
  item_id: number;
  item_name: string;
  category: string;
  quantity: number;
  note?: string;
  created_at?: string;
}

interface ShipmentDetail {
  id: number;
  order_id: number;
  order_details?: {
    partner_details?: {
      name?: string;
    };
    metadata?: {
      note?: string;
      transport_provider?: string;
    };
  };
  status: string;
  created_by: number;
  creator_name: string;
  packed_by?: number;
  packer_name?: string;
  number_of_cartons?: number;
  number_of_parcels?: number;
  transport_name?: string;
  bilty_number?: string;
  packed_at?: string;
  items: ShipmentItem[];
  created_at: string;
  updated_at: string;
}

const ShipmentView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Mark as Packed dialog state
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [packerName, setPackerName] = useState('');
  const [cartonCount, setCartonCount] = useState<number | ''>('');
  const [parcelCount, setParcelCount] = useState<number | ''>('');
  const [packing, setPacking] = useState(false);

  // Update Bilty dialog state
  const [biltyDialogOpen, setBiltyDialogOpen] = useState(false);
  const [transportName, setTransportName] = useState('');
  const [biltyNumber, setBiltyNumber] = useState('');
  const [updatingBilty, setUpdatingBilty] = useState(false);
  const [biltyError, setBiltyError] = useState<string | null>(null);

  // Export menu
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchShipment();
  }, [id]);

  const fetchShipment = async () => {
    try {
      const response = await api.get(`/api/linked-shipments/${id}`);
      setShipment(response.data);
    } catch (err) {
      console.error('Error fetching shipment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!shipment) return;

    const sortedItems = [...shipment.items].sort(
      (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
    );

    const rows: (string | number)[][] = [
      ['SHIPMENT DETAILS'],
      ['Order ID', `#${shipment.order_id}`],
      ['Shipment ID', `#${shipment.id}`],
      ['Customer', shipment.order_details?.partner_details?.name || 'N/A'],
    ];

    if (shipment.order_details?.metadata?.note) {
      rows.push(['Note', shipment.order_details.metadata.note]);
    }
    if (shipment.order_details?.metadata?.transport_provider) {
      rows.push(['Transport', shipment.order_details.metadata.transport_provider]);
    }

    rows.push([]);
    rows.push(['#', 'Item Name', 'Qty', 'Add']);

    sortedItems.forEach((item, index) => {
      rows.push([index + 1, item.item_name, item.quantity, '']);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 15 },
      { wch: 35 },
      { wch: 10 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shipment Items');

    XLSX.writeFile(workbook, `shipment-${shipment.id}-order-${shipment.order_id}.xlsx`);
    setExportAnchorEl(null);
  };

  const handlePrint = () => {
    if (!shipment) return;

    printShipment({
      shipmentId: shipment.id,
      orderId: shipment.order_id,
      customerName: shipment.order_details?.partner_details?.name || 'N/A',
      note: shipment.order_details?.metadata?.note,
      transport_provider: shipment.order_details?.metadata?.transport_provider,
      items: [...shipment.items].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()).map(item => ({
        item_name: item.item_name,
        quantity: item.quantity,
        note: item.note || ''
      })),
    });
  };

  const handleMarkAsPacked = async () => {
    if (!packerName.trim() || !cartonCount) {
      return;
    }

    setPacking(true);
    try {
      await api.post(`/api/linked-shipments/${id}/pack`, {
        packer_name: packerName,
        number_of_cartons: cartonCount,
        number_of_parcels: parcelCount,
      });
      
      // Refresh shipment data
      await fetchShipment();
      setPackDialogOpen(false);
      setPackerName('');
      setCartonCount('');
      setParcelCount('');
    } catch (err) {
      console.error('Error marking as packed:', err);
    } finally {
      setPacking(false);
    }
  };

  const handleOpenBiltyDialog = () => {
    // Pre-fill with existing values if available
    setTransportName(shipment?.transport_name || '');
    setBiltyNumber(shipment?.bilty_number || '');
    setBiltyDialogOpen(true);
  };

  const handleUpdateBilty = async () => {
    if (!transportName.trim() || !biltyNumber.trim()) {
      return;
    }

    setUpdatingBilty(true);
    setBiltyError(null);
    try {
      await api.patch(`/api/linked-shipments/${id}`, {
        transport_name: transportName.trim(),
        bilty_number: biltyNumber.trim(),
      });
      
      // Refresh shipment data
      await fetchShipment();
      setBiltyDialogOpen(false);
    } catch (err) {
      console.error('Error updating bilty:', err);
      setBiltyError('Failed to update bilty details. Please try again.');
    } finally {
      setUpdatingBilty(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case ShipmentStatus.PACKED:
        return <Chip icon={<CheckCircle />} label="Packed" color="success" />;
      case ShipmentStatus.CREATED:
        return <Chip label="Created" color="default" />;
      case ShipmentStatus.PENDING:
        return <Chip label="Pending" color="warning" />;
      default:
        return <Chip label={status} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shipment) {
    return <Typography sx={{ p: 3 }}>Shipment not found</Typography>;
  }

  const isPending = shipment.status === ShipmentStatus.PENDING || shipment.status === ShipmentStatus.CREATED;

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Shipments
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalShipping sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold">
              Shipment #{shipment.id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={handlePrint}
              size="small"
            >
              Print
            </Button>
            {shipment.status === ShipmentStatus.PACKED && (
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={handleOpenBiltyDialog}
                size="small"
              >
                Update Bilty
              </Button>
            )}
            {isPending && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => setPackDialogOpen(true)}
                size="small"
              >
                Mark as Packed
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              size="small"
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={handleExportExcel}>
                <TableChartOutlined sx={{ mr: 1 }} />
                Export as Excel
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Shipment Info */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Order ID
            </Typography>
            <Typography variant="h6">#{shipment.order_id}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Customer Name
            </Typography>
            <Typography variant="h6">{shipment.order_details?.partner_details?.name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>{getStatusChip(shipment.status)}</Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Created By
            </Typography>
            <Typography variant="body1">{shipment.creator_name}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1">
              {new Date(shipment.created_at).toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Order Note */}
        {shipment.order_details?.metadata?.note && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Order Note</Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic' }}>{shipment.order_details.metadata.note}</Typography>
          </Box>
        )}

        {/* Transport Provider */}
        {shipment.order_details?.metadata?.transport_provider && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Transport Preference</Typography>
            <Typography variant="body1" fontWeight="medium">{shipment.order_details.metadata.transport_provider}</Typography>
          </Box>
        )}

        {/* Packing & Bilty Info Row */}
        {(shipment.packer_name || shipment.transport_name || shipment.bilty_number) && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {/* Packing Info */}
            {shipment.packer_name && (
              <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Packing Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Packed By</Typography>
                    <Typography variant="body1" fontWeight="medium">{shipment.packer_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Cartons</Typography>
                    <Typography variant="body1" fontWeight="medium">{shipment.number_of_cartons} CTN</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Parcels</Typography>
                    <Typography variant="body1" fontWeight="medium">{shipment.number_of_parcels}</Typography>
                  </Box>
                  {shipment.packed_at && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Packed At</Typography>
                      <Typography variant="body1" fontWeight="medium">{new Date(shipment.packed_at).toLocaleString()}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Bilty Info */}
            {(shipment.transport_name || shipment.bilty_number) && (
              <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                  Bilty Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {shipment.transport_name && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Transport Name</Typography>
                      <Typography variant="body1" fontWeight="medium">{shipment.transport_name}</Typography>
                    </Box>
                  )}
                  {shipment.bilty_number && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Bilty Number</Typography>
                      <Typography variant="body1" fontWeight="medium">{shipment.bilty_number}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Items Table */}
        <Typography variant="h6" gutterBottom>
          Items to Pack
        </Typography>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                Quantity
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipment.items &&
              [...shipment.items].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()).map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.item_name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="center">{item.note || ''}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Mark as Packed Dialog */}
      <Dialog open={packDialogOpen} onClose={() => setPackDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as Packed</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Packer Name"
              value={packerName}
              onChange={(e) => setPackerName(e.target.value)}
              fullWidth
              required
              placeholder="Enter packer's name"
            />
            <TextField
              label="Number of Cartons (CTN)"
              type="number"
              value={cartonCount}
              onChange={(e) => setCartonCount(e.target.value ? parseInt(e.target.value) : '')}
              fullWidth
              required
              inputProps={{ min: 1 }}
              placeholder="Enter carton count"
            />
            <TextField
              label="Number of Parcels"
              type="number"
              value={parcelCount}
              onChange={(e) => setParcelCount(e.target.value ? parseInt(e.target.value) : '')}
              fullWidth
              inputProps={{ min: 0 }}
              placeholder="Enter parcel count"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackDialogOpen(false)} disabled={packing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkAsPacked}
            disabled={!packerName.trim() || !cartonCount || packing}
          >
            {packing ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Bilty Dialog */}
      <Dialog open={biltyDialogOpen} onClose={() => { setBiltyDialogOpen(false); setBiltyError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Update Bilty Details</DialogTitle>
        <DialogContent>
          {biltyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBiltyError(null)}>
              {biltyError}
            </Alert>
          )}
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Transport Name"
              value={transportName}
              onChange={(e) => setTransportName(e.target.value)}
              fullWidth
              required
              placeholder="Enter transport name"
            />
            <TextField
              label="Bilty Number"
              value={biltyNumber}
              onChange={(e) => setBiltyNumber(e.target.value)}
              fullWidth
              required
              placeholder="Enter bilty number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBiltyDialogOpen(false); setBiltyError(null); }} disabled={updatingBilty}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateBilty}
            disabled={!transportName.trim() || !biltyNumber.trim() || updatingBilty}
          >
            {updatingBilty ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipmentView;
